import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function SetupTwoFactor() {
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingQr, setLoadingQr] = useState(true);
  const { setAuthData } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    const tempToken = localStorage.getItem('tempToken');
    if (!tempToken) {
      navigate('/login');
      return;
    }
    api.post('/auth/totp/setup', {}, {
      headers: { Authorization: `Bearer ${tempToken}` }
    })
      .then(res => {
        setQrCode(res.data.qrCode);
        setSecret(res.data.secret);
      })
      .catch(() => {
        toast.error('QR-Code konnte nicht geladen werden');
        navigate('/login');
      })
      .finally(() => setLoadingQr(false));
  }, [navigate]);

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setLoading(true);
    try {
      const tempToken = localStorage.getItem('tempToken');
      const res = await api.post('/auth/totp/confirm', { code }, {
        headers: { Authorization: `Bearer ${tempToken}` }
      });
      const tempRefresh = localStorage.getItem('tempRefreshToken');
      localStorage.removeItem('tempToken');
      localStorage.removeItem('tempRefreshToken');
      const payload = JSON.parse(atob(res.data.accessToken.split('.')[1]));
      const userData = { id: payload.id, first_name: payload.first_name, last_name: payload.last_name, email: payload.email, role: payload.role, modules: payload.modules };
      setAuthData({ accessToken: res.data.accessToken, refreshToken: tempRefresh, user: userData });
      toast.success('2FA erfolgreich eingerichtet');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ungültiger Code');
      setCode('');
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sidebar flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white tracking-tight">Emge Computer</h1>
          <p className="text-sidebar-text mt-2 text-sm">Zwei-Faktor-Authentifizierung einrichten</p>
        </div>
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-1">Authenticator-App einrichten</h2>
              <p className="text-sm text-gray-500">Scannen Sie den QR-Code mit Ihrer Authenticator-App (z.B. Google Authenticator, Authy).</p>
            </div>

            <div className="flex justify-center">
              {loadingQr ? (
                <div className="w-48 h-48 bg-gray-100 rounded-xl flex items-center justify-center">
                  <span className="text-sm text-gray-400">Laden...</span>
                </div>
              ) : qrCode ? (
                <img src={qrCode} alt="QR-Code" className="w-48 h-48 rounded-xl border border-gray-200" />
              ) : null}
            </div>

            {secret && (
              <div className="bg-gray-50 rounded-xl p-3 text-center">
                <p className="text-xs text-gray-500 mb-1">Manueller Schlüssel</p>
                <p className="font-mono text-sm text-gray-700 tracking-widest break-all">{secret}</p>
              </div>
            )}

            <form onSubmit={handleConfirm} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Code zur Bestätigung
                </label>
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 transition-all"
                  placeholder="000000"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading || code.length !== 6 || loadingQr}
                className="w-full bg-primary-500 hover:bg-primary-600 text-white font-semibold py-3 px-4 rounded-xl text-sm transition-all disabled:opacity-50 shadow-lg shadow-primary-500/25"
              >
                {loading ? 'Wird eingerichtet...' : '2FA aktivieren'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
